import { t } from "@lingui/macro";
import { CircleNotch } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  KeyboardShortcut,
} from "@reactive-resume/ui";
import { useNavigate } from "react-router";

import { useLogout } from "@/client/services/auth";
import { useAuthStore } from "@/client/stores/auth";
import { useBillingPortal } from "@/client/services/billing";
import { useToast } from "@/client/hooks/use-toast";

type Props = {
  children: React.ReactNode;
};

export const UserOptions = ({ children }: Props) => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const user = useAuthStore((state) => state.user);
  const { getBillingPortal, isLoading } = useBillingPortal();
  const { toast } = useToast();

  const handleManageBilling = async () => {
    try {
      const { url } = await getBillingPortal();

      toast({
        title: t`Redirecting to billing portal`,
        description: t`You will be redirected to the Stripe billing portal in a moment.`,
      });

      // Short delay to show the toast before redirecting
      setTimeout(() => {
        window.location.href = url;
      }, 1500);
    } catch (error) {
      console.error("Failed to get billing portal URL:", error);

      toast({
        variant: "error",
        title: t`Error`,
        description: t`Failed to get billing portal URL. Please try again.`,
      });
    }
  };

  if (user?.isAnonymous) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

        <DropdownMenuContent side="top" align="start" className="w-48">
          <DropdownMenuItem
            onClick={() => {
              void navigate("/auth/register");
            }}
          >
            {t`Create Account or Login`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // For authenticated users, show the full menu
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" className="w-48">
        <DropdownMenuItem
          onClick={() => {
            void navigate("/dashboard/settings");
          }}
        >
          {t`Settings`}
          {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
          <KeyboardShortcut>⇧S</KeyboardShortcut>
        </DropdownMenuItem>

        {user?.plusUser ? (
          <DropdownMenuItem onClick={handleManageBilling} disabled={isLoading}>
            {isLoading ? (
              <>
                <CircleNotch className="mr-2 size-4 animate-spin" />
                {t`Loading...`}
              </>
            ) : (
              t`Manage Subscription`
            )}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => {
              void navigate("/dashboard/upgrade");
            }}
          >
            {t`Upgrade to Plus`}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>
          {t`Logout`}
          {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
          <KeyboardShortcut>⇧Q</KeyboardShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
